import {
  Combobox,
  ComboboxContent,
  ComboboxGroup,
  ComboboxItem,
  ComboboxSearchField,
  ComboboxTrigger,
  ComboboxTriggerLoading,
  ComboboxValue,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  InputMask,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@barfacil/barfacil-ui'
import { useState } from 'react'
import { useFormContext } from 'react-hook-form'

import {
  CategoryOptionsResponse,
  Image,
  PriceType,
  Product,
  Status,
} from '@/api/_schemas/management'
import { FormWithTitleContainer } from '@/app/panel/management/price-list/_components/form-with-title-container'
import { PriceListProductImageDialog } from '@/app/panel/management/price-list/_components/price-list-product-image-dialog'
import { usePriceListCategoryOptions } from '@/app/panel/management/price-list/_hooks/use-category-options'
import { FormUpdateProduct } from '@/app/panel/management/price-list/_schemas/update-product'
import { cn, removeAccents } from '@/lib/utils'

type UpdateProductGeralFormProps = {
  product: Product
}

export const UpdateProductGeralForm = ({
  product,
}: UpdateProductGeralFormProps) => {
  const [selectedImage, setSelectedImage] = useState<Image>(
    product.imagem && product.imagem_path
      ? {
          imagem: product.imagem,
          imagem_path: product.imagem_path,
          descricao: product.nome,
        }
      : null,
  )

  const [openComboboxCategory, setOpenComboboxCategory] = useState(false)
  const [categorySearch, setCategorySearch] = useState('')
  const [filteredCategories, setFilteredCategories] =
    useState<CategoryOptionsResponse>([])

  const { data: categoryOptions, isLoading: isLoadingCategoryOptions } =
    usePriceListCategoryOptions()

  const form = useFormContext<FormUpdateProduct>()

  const priceTypesArray: [string, PriceType][] = Object.entries(PriceType)
    .filter(([key, value]) => isNaN(Number(key)) && value !== PriceType.Vazio)
    .map(([key, value]) => [key, value] as [string, PriceType])

  function handleCategorySearchChange(
    event: React.ChangeEvent<HTMLInputElement>,
  ) {
    const value = event.currentTarget.value

    setCategorySearch(value)
    setFilteredCategories(
      categoryOptions?.filter(({ nome }) =>
        removeAccents(nome.toUpperCase()).includes(
          removeAccents(value.toUpperCase()),
        ),
      ) ?? [],
    )
  }

  function handleOnSaveImage(image: Image) {
    setSelectedImage(image)
    form.setValue('imagem', image?.imagem)
  }

  return (
    <div className="flex flex-col-reverse gap-4 sm:flex-col sm:gap-2">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-2 lg:grid-cols-3">
        <FormField
          control={form.control}
          name="nome"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Nome</FormLabel>

              <FormControl>
                <Input
                  autoFocus
                  placeholder="Produto"
                  {...field}
                  disabled={form.formState.isSubmitting}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="produto_pai_nome"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Categoria</FormLabel>

              <Combobox
                open={openComboboxCategory}
                onOpenChange={setOpenComboboxCategory}
                onValueChange={field.onChange}
                defaultValue={field.value}
                focusSelected={false}
                disabled={isLoadingCategoryOptions}
              >
                <FormControl>
                  {isLoadingCategoryOptions ? (
                    <ComboboxTriggerLoading>
                      Carregando...
                    </ComboboxTriggerLoading>
                  ) : (
                    <ComboboxTrigger
                      onBlur={() => setCategorySearch('')}
                      disabled={form.formState.isSubmitting}
                    >
                      <ComboboxValue placeholder="Informe o nome da categoria">
                        {field.value}
                      </ComboboxValue>
                    </ComboboxTrigger>
                  )}
                </FormControl>

                <ComboboxContent>
                  <ComboboxSearchField
                    value={categorySearch}
                    autoComplete="off"
                    loading={isLoadingCategoryOptions}
                    onChange={handleCategorySearchChange}
                  />

                  <ComboboxGroup>
                    {(!categorySearch
                      ? categoryOptions
                      : filteredCategories
                    )?.map(({ cod_produto: id, nome }) => (
                      <ComboboxItem
                        key={id}
                        value={nome}
                        onClick={() => {
                          form.setValue('produto_pai', id, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                          form.setValue('produto_pai_nome', nome, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                          setOpenComboboxCategory(false)
                        }}
                      >
                        {nome}
                      </ComboboxItem>
                    ))}
                  </ComboboxGroup>
                </ComboboxContent>
              </Combobox>

              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="vlr_custo"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Valor de custo</FormLabel>

              <FormControl>
                <InputMask
                  {...field}
                  mask="currency"
                  disabled={form.formState.isSubmitting}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="tipo_valor"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tipo de valor</FormLabel>

              <Select
                onValueChange={field.onChange}
                defaultValue={(field.value !== PriceType.Vazio
                  ? field.value
                  : ''
                ).toString()}
              >
                <FormControl>
                  <SelectTrigger disabled={form.formState.isSubmitting}>
                    <SelectValue placeholder="Selecione o tipo de valor" />
                  </SelectTrigger>
                </FormControl>

                <SelectContent>
                  <SelectGroup>
                    {priceTypesArray.map(([key, value]) => (
                      <SelectItem
                        className="flex gap-1"
                        key={key}
                        value={value.toString()}
                      >
                        {key}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>

              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="situacao"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Situação</FormLabel>

              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger disabled={form.formState.isSubmitting}>
                    <SelectValue placeholder="Selecione a situação" />
                  </SelectTrigger>
                </FormControl>

                <SelectContent>
                  <SelectGroup>
                    <SelectItem className="flex gap-1" value={Status.Ativo}>
                      {Status.Ativo}
                    </SelectItem>
                    <SelectItem className="flex gap-1" value={Status.Inativo}>
                      {Status.Inativo}
                    </SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>

              <FormMessage />
            </FormItem>
          )}
        />

        <div className="hidden sm:block 2xl:hidden" />

        <aside className="flex flex-col gap-4 sm:flex-row sm:gap-2">
          <div className="flex flex-col-reverse sm:py-2">
            <FormField
              control={form.control}
              name="consumo"
              render={({ field }) => (
                <FormItem className="flex w-full items-center gap-2 space-y-0">
                  <FormControl>
                    <Switch
                      className="m-0"
                      id="consumption"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={form.formState.isSubmitting}
                    />
                  </FormControl>

                  <FormLabel className="line-clamp-2" htmlFor="consumption">
                    Consumo
                  </FormLabel>
                </FormItem>
              )}
            />
          </div>

          <div className="flex flex-col-reverse sm:py-2">
            <FormField
              control={form.control}
              name="permitir_observacao"
              render={({ field }) => (
                <FormItem className="flex w-full items-center gap-2 space-y-0">
                  <FormControl>
                    <Switch
                      className="m-0"
                      id="observation"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={form.formState.isSubmitting}
                    />
                  </FormControl>

                  <FormLabel className="line-clamp-2" htmlFor="observation">
                    Observação
                  </FormLabel>
                </FormItem>
              )}
            />
          </div>

          <div className="flex flex-col-reverse sm:py-2">
            <FormField
              control={form.control}
              name="servico"
              render={({ field }) => (
                <FormItem className="flex w-full items-center gap-2 space-y-0">
                  <FormControl>
                    <Switch
                      className="m-0"
                      id="service"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={form.formState.isSubmitting}
                    />
                  </FormControl>

                  <FormLabel className="line-clamp-2" htmlFor="service">
                    Serviço
                  </FormLabel>
                </FormItem>
              )}
            />
          </div>
        </aside>
      </div>

      <FormWithTitleContainer title="ADICIONAR/ALTERAR IMAGEM">
        <div className="space-y-1">
          <PriceListProductImageDialog
            className={cn(
              'mb-0',
              !!form.formState.errors.imagem && 'border border-red-200',
            )}
            product={{ id: product.cod_produto, produto: product.nome }}
            image={selectedImage}
            onSave={handleOnSaveImage}
          />

          {!!form.formState.errors.imagem && (
            <p className="text-xs font-medium text-red-500">
              {form.formState.errors.imagem?.message}
            </p>
          )}
        </div>
      </FormWithTitleContainer>
    </div>
  )
}
